import React, { useState, useEffect } from 'react';
import { TldInfo } from '../types';
import ApiService from '../services/api';
import FluentIcon from '../components/FluentIcon';
import config from '../config';

const TldList: React.FC = () => {
  const [tldList, setTldList] = useState<TldInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProtocol, setSelectedProtocol] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [editingTld, setEditingTld] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    supported_protocol: string;
    server: string;
    rdap_server: string;
    is_active: boolean;
  }>({
    supported_protocol: '',
    server: '',
    rdap_server: '',
    is_active: true
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<{
    tld: string;
    supported_protocol: string;
    server: string;
    rdap_server: string;
    is_active: boolean;
  }>({
    tld: '',
    supported_protocol: 'both',
    server: '',
    rdap_server: '',
    is_active: true
  });
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Validation functions
  const validateForm = (form: any, isEdit: boolean = false) => {
    const errors: {[key: string]: string} = {};

    // If not active, skip validation for other fields
    if (!form.is_active) {
      setValidationErrors({});
      return true;
    }

    // Protocol-based validation
    if (form.supported_protocol === 'both') {
      if (!form.server || form.server.trim() === '') {
        errors.server = 'WHOIS server is required when protocol is "Both"';
      }
      if (!form.rdap_server || form.rdap_server.trim() === '') {
        errors.rdap_server = 'RDAP server is required when protocol is "Both"';
      }
    } else if (form.supported_protocol === 'whois') {
      if (!form.server || form.server.trim() === '') {
        errors.server = 'WHOIS server is required when protocol is "WHOIS"';
      }
    } else if (form.supported_protocol === 'rdap') {
      if (!form.rdap_server || form.rdap_server.trim() === '') {
        errors.rdap_server = 'RDAP server is required when protocol is "RDAP"';
      }
    }

    // TLD validation for create form
    if (!isEdit && (!form.tld || form.tld.trim() === '')) {
      errors.tld = 'TLD is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getFieldProps = (fieldName: string, form: any) => {
    const isInactive = !form.is_active;
    const protocol = form.supported_protocol;
    
    let isReadOnly = isInactive;
    let isRequired = false;

    if (!isInactive) {
      switch (fieldName) {
        case 'server':
          isReadOnly = protocol === 'rdap';
          isRequired = protocol === 'whois' || protocol === 'both';
          break;
        case 'rdap_server':
          isReadOnly = protocol === 'whois';
          isRequired = protocol === 'rdap' || protocol === 'both';
          break;
        case 'supported_protocol':
          isReadOnly = false;
          isRequired = true;
          break;
        case 'tld':
          isReadOnly = false;
          isRequired = true;
          break;
      }
    }

    return {
      readOnly: isReadOnly,
      required: isRequired,
      disabled: isInactive,
      style: {
        backgroundColor: isReadOnly || isInactive ? '#f9fafb' : 'white',
        color: isReadOnly || isInactive ? '#6b7280' : '#374151',
        cursor: isReadOnly || isInactive ? 'not-allowed' : 'text',
        opacity: isReadOnly || isInactive ? 0.6 : 1
      }
    };
  };

  useEffect(() => {
    const fetchTlds = async () => {
      try {
        setLoading(true);
        const data = await ApiService.getTlds();
        setTldList(data);
        setError(null);
      } catch (error) {
        console.error('Error fetching TLD data:', error);
        setError('Failed to fetch TLD data');
      } finally {
        setLoading(false);
      }
    };

    fetchTlds();
  }, []);

  const startEdit = (tld: TldInfo) => {
    setEditingTld(tld.tld);
    
    // Get optimal server suggestions based on TLD
    const getOptimalWhoisServer = (tldName: string) => {
      const commonServers: {[key: string]: string} = {
        'com': 'whois.verisign-grs.com',
        'net': 'whois.verisign-grs.com',
        'org': 'whois.pir.org',
        'info': 'whois.afilias.net',
        'biz': 'whois.nic.biz',
        'us': 'whois.nic.us',
        'uk': 'whois.nic.uk',
        'ca': 'whois.cira.ca',
        'au': 'whois.auda.org.au',
        'de': 'whois.denic.de',
        'fr': 'whois.afnic.fr',
        'it': 'whois.nic.it',
        'nl': 'whois.domain-registry.nl',
        'be': 'whois.dns.be',
        'io': 'whois.nic.io',
        'ai': 'whois.nic.ai',
        'co': 'whois.nic.co',
        'me': 'whois.nic.me',
        'tv': 'whois.nic.tv'
      };
      return commonServers[tldName] || `whois.nic.${tldName}`;
    };

    const getOptimalRdapServer = (tldName: string) => {
      const commonRdapServers: {[key: string]: string} = {
        'com': 'https://rdap.verisign.com/com/v1/',
        'net': 'https://rdap.verisign.com/net/v1/',
        'org': 'https://rdap.publicinterestregistry.org/',
        'info': 'https://rdap.afilias.info/',
        'biz': 'https://rdap.nic.biz/',
        'us': 'https://rdap.nic.us/',
        'uk': 'https://rdap.nominet.uk/',
        'ca': 'https://rdap.ca/',
        'au': 'https://rdap.nic.au/',
        'de': 'https://rdap.denic.de/',
        'fr': 'https://rdap.nic.fr/',
        'it': 'https://rdap.nic.it/',
        'nl': 'https://rdap.nl/',
        'be': 'https://rdap.nic.be/',
        'io': 'https://rdap.nic.io/',
        'ai': 'https://rdap.nic.ai/',
        'co': 'https://rdap.nic.co/',
        'me': 'https://rdap.nic.me/',
        'tv': 'https://rdap.nic.tv/'
      };
      return commonRdapServers[tldName] || `https://rdap.nic.${tldName}/`;
    };

    setEditForm({
      supported_protocol: tld.supported_protocol,
      server: tld.server || getOptimalWhoisServer(tld.tld),
      rdap_server: tld.rdap_server || getOptimalRdapServer(tld.tld),
      is_active: tld.is_active
    });
  };

  const cancelEdit = () => {
    setEditingTld(null);
    setEditForm({
      supported_protocol: '',
      server: '',
      rdap_server: '',
      is_active: true
    });
    setValidationErrors({});
  };

  const startCreate = () => {
    setShowCreateForm(true);
    setCreateForm({
      tld: '',
      supported_protocol: 'both',
      server: '',
      rdap_server: '',
      is_active: true
    });
  };

  const cancelCreate = () => {
          setShowCreateForm(false);
      setCreateForm({
        tld: '',
        supported_protocol: 'both',
        server: '',
        rdap_server: '',
        is_active: true
      });
      setValidationErrors({});
  };

  const saveCreate = async () => {
    if (!validateForm(createForm, false)) {
      return;
    }

    try {
      // Get optimal server suggestions based on TLD
      const getOptimalWhoisServer = (tldName: string) => {
        const commonServers: {[key: string]: string} = {
          'com': 'whois.verisign-grs.com',
          'net': 'whois.verisign-grs.com',
          'org': 'whois.pir.org',
          'info': 'whois.afilias.net',
          'biz': 'whois.nic.biz',
          'us': 'whois.nic.us',
          'uk': 'whois.nic.uk',
          'ca': 'whois.cira.ca',
          'au': 'whois.auda.org.au',
          'de': 'whois.denic.de',
          'fr': 'whois.afnic.fr',
          'it': 'whois.nic.it',
          'nl': 'whois.domain-registry.nl',
          'be': 'whois.dns.be',
          'io': 'whois.nic.io',
          'ai': 'whois.nic.ai',
          'co': 'whois.nic.co',
          'me': 'whois.nic.me',
          'tv': 'whois.nic.tv'
        };
        return commonServers[tldName] || `whois.nic.${tldName}`;
      };

      const getOptimalRdapServer = (tldName: string) => {
        const commonRdapServers: {[key: string]: string} = {
          'com': 'https://rdap.verisign.com/com/v1/',
          'net': 'https://rdap.verisign.com/net/v1/',
          'org': 'https://rdap.publicinterestregistry.org/',
          'info': 'https://rdap.afilias.info/',
          'biz': 'https://rdap.nic.biz/',
          'us': 'https://rdap.nic.us/',
          'uk': 'https://rdap.nominet.uk/',
          'ca': 'https://rdap.ca/',
          'au': 'https://rdap.nic.au/',
          'de': 'https://rdap.denic.de/',
          'fr': 'https://rdap.nic.fr/',
          'it': 'https://rdap.nic.it/',
          'nl': 'https://rdap.nl/',
          'be': 'https://rdap.nic.be/',
          'io': 'https://rdap.nic.io/',
          'ai': 'https://rdap.nic.ai/',
          'co': 'https://rdap.nic.co/',
          'me': 'https://rdap.nic.me/',
          'tv': 'https://rdap.nic.tv/'
        };
        return commonRdapServers[tldName] || `https://rdap.nic.${tldName}/`;
      };

      const tldName = createForm.tld.toLowerCase().replace(/^\./, '');
      
      await ApiService.createTld({
        tld: tldName,
        supported_protocol: createForm.supported_protocol,
        server: createForm.server || getOptimalWhoisServer(tldName),
        rdap_server: createForm.rdap_server || getOptimalRdapServer(tldName),
        is_active: createForm.is_active
      });

      // Refresh the TLD list
      const data = await ApiService.getTlds();
      setTldList(data);

      // Reset form
      cancelCreate();
    } catch (error) {
      console.error('Error creating TLD:', error);
      alert('Failed to create TLD. It may already exist.');
    }
  };

  const saveEdit = async () => {
    if (!editingTld) return;
    
    if (!validateForm(editForm, true)) {
      return;
    }

    try {
      await ApiService.updateTld(editingTld, {
        supported_protocol: editForm.supported_protocol,
        server: editForm.server,
        rdap_server: editForm.rdap_server || undefined,
        is_active: editForm.is_active
      });

      // Update local state
      setTldList(prev => prev.map(tld => 
        tld.tld === editingTld 
          ? { ...tld, ...editForm, rdap_server: editForm.rdap_server || undefined }
          : tld
      ));

      setEditingTld(null);
      setEditForm({
        supported_protocol: '',
        server: '',
        rdap_server: '',
        is_active: true
      });
      setValidationErrors({});
    } catch (error) {
      console.error('Error updating TLD:', error);
      alert('Failed to update TLD');
    }
  };

  const deleteTld = async (tldName: string) => {
    if (!window.confirm(`Are you sure you want to delete the TLD "${tldName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${config.apiUrl}/api/tlds/${tldName}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Remove from local state
        setTldList(prev => prev.filter(tld => tld.tld !== tldName));
        setError(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to delete TLD');
      }
    } catch (error) {
      console.error('Error deleting TLD:', error);
      setError('Error deleting TLD');
    }
  };

  const getAllTlds = () => {
    return tldList.map(tld => ({
      tld: tld.tld,
      protocol: tld.supported_protocol.toUpperCase(),
      server: tld.server,
      rdap_server: tld.rdap_server || '',
      is_active: tld.is_active,
      full_tld: tld
    }));
  };

  const filteredTlds = getAllTlds().filter(item => {
    const matchesSearch = item.tld.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProtocol = selectedProtocol === 'all' || item.protocol.toLowerCase() === selectedProtocol;
    const matchesStatus = selectedStatus === 'all' || 
      (selectedStatus === 'active' && item.is_active) || 
      (selectedStatus === 'inactive' && !item.is_active);
    return matchesSearch && matchesProtocol && matchesStatus;
  });

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (error && tldList.length === 0) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        color: '#dc2626',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600 }}>Error</h3>
        <p style={{ margin: 0, fontSize: '14px' }}>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            marginTop: '12px',
            padding: '8px 16px',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px', color: '#374151' }}><FluentIcon name="globe" size={24} /></div>
          <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0' }}>
            {tldList.length.toLocaleString()}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Total TLDs</p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px', color: '#374151' }}><FluentIcon name="link" size={24} /></div>
          <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0' }}>
            {tldList.filter(tld => tld.supported_protocol === 'whois' || tld.supported_protocol === 'both').length}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>WHOIS Servers</p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px', color: '#374151' }}><FluentIcon name="data-bar-vertical" size={24} /></div>
          <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0' }}>
            {tldList.filter(tld => tld.supported_protocol === 'rdap' || tld.supported_protocol === 'both').length}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>RDAP Servers</p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px', color: '#374151' }}><FluentIcon name="checkmark-circle" size={24} /></div>
          <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0' }}>
            {tldList.filter(tld => tld.is_active).length}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Active TLDs</p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px', color: '#374151' }}><FluentIcon name="dismiss-circle" size={24} /></div>
          <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0' }}>
            {tldList.filter(tld => !tld.is_active).length}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Inactive TLDs</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Search TLD
            </label>
            <input
              type="text"
              placeholder="Search for a TLD (e.g., com, org, uk)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Protocol
            </label>
            <select
              value={selectedProtocol}
              onChange={(e) => setSelectedProtocol(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'white'
              }}
            >
              <option value="all">All Protocols</option>
              <option value="whois">WHOIS Only</option>
              <option value="rdap">RDAP Only</option>
              <option value="both">Both</option>
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'white'
              }}
            >
              <option value="all">All TLDs</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          <div>
            <button
              onClick={startCreate}
              style={{
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                height: '48px'
              }}
            >
              <FluentIcon name="add" size={16} />
              Add New TLD
            </button>
          </div>
        </div>
      </div>

      {/* Create TLD Form */}
      {showCreateForm && (
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 16px 0'
          }}>
            Add New TLD
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '20px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                TLD *
              </label>
              <input
                type="text"
                placeholder="e.g., example, test, custom"
                value={createForm.tld}
                onChange={(e) => {
                  setCreateForm(prev => ({ ...prev, tld: e.target.value }));
                  setValidationErrors(prev => ({ ...prev, tld: '' }));
                }}
                {...getFieldProps('tld', createForm)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: validationErrors.tld ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  ...getFieldProps('tld', createForm).style
                }}
              />
              {validationErrors.tld && (
                <p style={{ color: '#ef4444', fontSize: '12px', margin: '4px 0 0 0' }}>
                  {validationErrors.tld}
                </p>
              )}
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Protocol *
              </label>
              <select
                value={createForm.supported_protocol}
                onChange={(e) => {
                  const newProtocol = e.target.value;
                  setCreateForm(prev => ({ 
                    ...prev, 
                    supported_protocol: newProtocol,
                    // Clear RDAP server if WHOIS is selected
                    rdap_server: newProtocol === 'whois' ? '' : prev.rdap_server,
                    // Clear WHOIS server if RDAP is selected
                    server: newProtocol === 'rdap' ? '' : prev.server
                  }));
                  setValidationErrors({});
                }}
                {...getFieldProps('supported_protocol', createForm)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  ...getFieldProps('supported_protocol', createForm).style
                }}
              >
                <option value="whois">WHOIS</option>
                <option value="rdap">RDAP</option>
                <option value="both">Both</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                WHOIS Server {getFieldProps('server', createForm).required && '*'}
              </label>
              <input
                type="text"
                placeholder="whois.nic.example"
                value={createForm.server}
                onChange={(e) => {
                  setCreateForm(prev => ({ ...prev, server: e.target.value }));
                  setValidationErrors(prev => ({ ...prev, server: '' }));
                }}
                {...getFieldProps('server', createForm)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: validationErrors.server ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: 'monospace',
                  ...getFieldProps('server', createForm).style
                }}
              />
              {validationErrors.server && (
                <p style={{ color: '#ef4444', fontSize: '12px', margin: '4px 0 0 0' }}>
                  {validationErrors.server}
                </p>
              )}
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                RDAP Server {getFieldProps('rdap_server', createForm).required && '*'}
              </label>
              <input
                type="text"
                placeholder="https://rdap.nic.example/"
                value={createForm.rdap_server}
                onChange={(e) => {
                  setCreateForm(prev => ({ ...prev, rdap_server: e.target.value }));
                  setValidationErrors(prev => ({ ...prev, rdap_server: '' }));
                }}
                {...getFieldProps('rdap_server', createForm)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: validationErrors.rdap_server ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: 'monospace',
                  ...getFieldProps('rdap_server', createForm).style
                }}
              />
              {validationErrors.rdap_server && (
                <p style={{ color: '#ef4444', fontSize: '12px', margin: '4px 0 0 0' }}>
                  {validationErrors.rdap_server}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={createForm.is_active}
                  onChange={(e) => {
                    setCreateForm(prev => ({ ...prev, is_active: e.target.checked }));
                    setValidationErrors({});
                  }}
                  className="form-checkbox"
                />
                <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Active</span>
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={saveCreate}
              style={{
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FluentIcon name="save" size={16} />
              Create TLD
            </button>
            <button
              onClick={cancelCreate}
              style={{
                padding: '12px 20px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FluentIcon name="dismiss" size={16} />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* TLD List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: 0
          }}>
            Supported TLDs Configuration ({filteredTlds.length})
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '4px 0 0 0'
          }}>
            Manage TLD configurations for WHOIS and RDAP servers
          </p>
        </div>

        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {filteredTlds.length === 0 ? (
            <div style={{
              padding: '48px 24px',
              textAlign: 'center',
              color: '#6b7280'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}><FluentIcon name="search" size={48} /></div>
              <p style={{ fontSize: '16px', margin: 0 }}>No TLDs found matching your search criteria.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: '#374151',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      TLD
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: '#374151',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Protocol
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: '#374151',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      WHOIS Server
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: '#374151',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      RDAP Server
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'center', 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: '#374151',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Status
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'center', 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: '#374151',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTlds.map((item, index) => (
                    <React.Fragment key={item.tld}>
                      <tr style={{ 
                        borderBottom: '1px solid #f3f4f6',
                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                      }}>
                        <td style={{ padding: '16px', verticalAlign: 'top' }}>
                          <span style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#111827',
                            fontFamily: 'monospace'
                          }}>
                            .{item.tld}
                          </span>
                        </td>
                        
                        <td style={{ padding: '16px', verticalAlign: 'top' }}>
                          {editingTld === item.tld ? (
                            <select
                              value={editForm.supported_protocol}
                              onChange={(e) => {
                                setEditForm(prev => ({ ...prev, supported_protocol: e.target.value }));
                                setValidationErrors({});
                              }}
                              {...getFieldProps('supported_protocol', editForm)}
                              style={{
                                width: '120px',
                                padding: '6px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px',
                                outline: 'none',
                                ...getFieldProps('supported_protocol', editForm).style
                              }}
                            >
                              <option value="whois">WHOIS</option>
                              <option value="rdap">RDAP</option>
                              <option value="both">Both</option>
                            </select>
                          ) : (
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: item.protocol === 'WHOIS' ? '#059669' : item.protocol === 'RDAP' ? '#3b82f6' : '#7c3aed',
                              backgroundColor: item.protocol === 'WHOIS' ? '#f0fdf4' : item.protocol === 'RDAP' ? '#eff6ff' : '#f3e8ff',
                              padding: '4px 8px',
                              borderRadius: '4px'
                            }}>
                              {item.protocol}
                            </span>
                          )}
                        </td>

                        <td style={{ padding: '16px', verticalAlign: 'top' }}>
                          {editingTld === item.tld ? (
                            <div>
                              <input
                                type="text"
                                value={editForm.server}
                                onChange={(e) => {
                                  setEditForm(prev => ({ ...prev, server: e.target.value }));
                                  setValidationErrors(prev => ({ ...prev, server: '' }));
                                }}
                                placeholder={`e.g., whois.nic.${item.tld}`}
                                {...getFieldProps('server', editForm)}
                                style={{
                                  width: '200px',
                                  padding: '6px 8px',
                                  border: validationErrors.server ? '1px solid #ef4444' : '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontFamily: 'monospace',
                                  ...getFieldProps('server', editForm).style
                                }}
                              />
                              {validationErrors.server && (
                                <p style={{ color: '#ef4444', fontSize: '11px', margin: '2px 0 0 0' }}>
                                  {validationErrors.server}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span style={{
                              fontSize: '14px',
                              color: item.server ? '#6b7280' : '#9ca3af',
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                              fontStyle: item.server ? 'normal' : 'italic'
                            }}>
                              {item.server || 'Not configured'}
                            </span>
                          )}
                        </td>

                        <td style={{ padding: '16px', verticalAlign: 'top' }}>
                          {editingTld === item.tld ? (
                            <div>
                              <input
                                type="text"
                                value={editForm.rdap_server}
                                onChange={(e) => {
                                  setEditForm(prev => ({ ...prev, rdap_server: e.target.value }));
                                  setValidationErrors(prev => ({ ...prev, rdap_server: '' }));
                                }}
                                placeholder={`e.g., https://rdap.nic.${item.tld}/`}
                                {...getFieldProps('rdap_server', editForm)}
                                style={{
                                  width: '200px',
                                  padding: '6px 8px',
                                  border: validationErrors.rdap_server ? '1px solid #ef4444' : '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontFamily: 'monospace',
                                  ...getFieldProps('rdap_server', editForm).style
                                }}
                              />
                              {validationErrors.rdap_server && (
                                <p style={{ color: '#ef4444', fontSize: '11px', margin: '2px 0 0 0' }}>
                                  {validationErrors.rdap_server}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span style={{
                              fontSize: '14px',
                              color: item.rdap_server ? '#6b7280' : '#9ca3af',
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                              fontStyle: item.rdap_server ? 'normal' : 'italic'
                            }}>
                              {item.rdap_server || 'Not configured'}
                            </span>
                          )}
                        </td>

                        <td style={{ padding: '16px', textAlign: 'center', verticalAlign: 'top' }}>
                          {editingTld === item.tld ? (
                            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                              <input
                                type="checkbox"
                                checked={editForm.is_active}
                                onChange={(e) => {
                                  setEditForm(prev => ({ ...prev, is_active: e.target.checked }));
                                  setValidationErrors({});
                                }}
                                className="form-checkbox"
                              />
                              <span style={{ fontSize: '14px', color: '#374151' }}>Active</span>
                            </label>
                          ) : (
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: item.is_active ? '#059669' : '#dc2626',
                              backgroundColor: item.is_active ? '#f0fdf4' : '#fef2f2',
                              padding: '4px 8px',
                              borderRadius: '4px'
                            }}>
                              {item.is_active ? 'Active' : 'Inactive'}
                            </span>
                          )}
                        </td>
                        
                        <td style={{ padding: '16px', textAlign: 'center', verticalAlign: 'top' }}>
                          {editingTld === item.tld ? (
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                onClick={saveEdit}
                                style={{
                                  padding: '6px 12px',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <FluentIcon name="save" size={14} /> Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <FluentIcon name="dismiss" size={14} /> Cancel
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                onClick={() => startEdit(item.full_tld)}
                                style={{
                                  padding: '8px 16px',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  minWidth: '80px',
                                  justifyContent: 'center',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <FluentIcon name="edit" size={14} />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => deleteTld(item.tld)}
                                style={{
                                  padding: '8px 16px',
                                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  minWidth: '80px',
                                  justifyContent: 'center',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <FluentIcon name="delete" size={14} />
                                <span>Delete</span>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TldList; 